import type { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';

import type { N8nManagerApi } from '../../api/n8nmanager.api';
import type {
  CreateOrderInput,
  CreateOrderResponse,
  GetOrderByIdResponse,
  GetOrdersParams,
  GetPaginatedOrdersResponse,
  UpdateOrderBackstageBodyInput,
  UpdateOrderResponse,
  RequestDeletionBodyInput,
  OrderResponse,
  CheckUriPrefixResponse,
  CheckExistingByCostCenterResponse,
} from '../../api/n8nmanager.api.objects';
/**
 * Concrete implementation of {@link N8nManagerApi} that talks to the
 * N8N Instance Manager via the Backstage proxy (`/api/n8nmanager`).
 */
export class N8nManagerService implements N8nManagerApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(discoveryApi: DiscoveryApi, fetchApi: FetchApi) {
    this.discoveryApi = discoveryApi;
    this.fetchApi = fetchApi;
  }

  private async getProxyBase(): Promise<string> {
    return this.discoveryApi.getBaseUrl('n8nmanager');
  }

  // ── GET /v1/orders ──────────────────────────────────────────────────────────

  async getOrders(
    params: GetOrdersParams = {},
  ): Promise<GetPaginatedOrdersResponse> {
    const proxyBase = await this.getProxyBase();

    const query = new URLSearchParams();
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.pageSize !== undefined)
      query.set('pageSize', String(params.pageSize));
    if (params.state) query.set('state', params.state);
    if (params.sortBy) query.set('sortBy', params.sortBy);
    if (params.sortOrder) query.set('sortOrder', params.sortOrder);

    const qs = query.toString();
    const ordersUrl = `${proxyBase}/v1/orders`;
    const url = qs ? `${ordersUrl}?${qs}` : ordersUrl;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(
        `N8N Manager API error (getOrders): ${response.status} ${response.statusText}`,
      );
    }

    return response.json() as Promise<GetPaginatedOrdersResponse>;
  }

  // ── GET /v1/orders/{id} ─────────────────────────────────────────────────────

  async getOrderById(id: string): Promise<GetOrderByIdResponse> {
    const proxyBase = await this.getProxyBase();
    const url = `${proxyBase}/v1/orders/${encodeURIComponent(id)}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(
        `N8N Manager API error (getOrderById): ${response.status} ${response.statusText}`,
      );
    }

    return response.json() as Promise<GetOrderByIdResponse>;
  }

  // ── POST /v1/orders ─────────────────────────────────────────────────────────

  async createOrder(input: CreateOrderInput): Promise<CreateOrderResponse> {
    const proxyBase = await this.getProxyBase();
    const url = `${proxyBase}/v1/orders`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      throw new Error(
        `N8N Manager API error (createOrder): ${response.status} ${response.statusText}`,
      );
    }

    return response.json() as Promise<CreateOrderResponse>;
  }

  // ── PATCH /v1/orders/{id} ───────────────────────────────────────────────────

  async updateOrder(
    id: string,
    input: UpdateOrderBackstageBodyInput,
  ): Promise<UpdateOrderResponse> {
    const proxyBase = await this.getProxyBase();
    const url = `${proxyBase}/v1/orders/${encodeURIComponent(id)}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      throw new Error(
        `N8N Manager API error (updateOrder): ${response.status} ${response.statusText}`,
      );
    }

    return response.json() as Promise<UpdateOrderResponse>;
  }

  // ── POST /v1/orders/{id}/request-deletion ───────────────────────────────────

  async requestDeletion(
    id: string,
    input: RequestDeletionBodyInput,
  ): Promise<OrderResponse> {
    const proxyBase = await this.getProxyBase();
    const url = `${proxyBase}/v1/orders/${encodeURIComponent(id)}/request-deletion`;

    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });

    if (!response.ok) {
      throw new Error(
        `N8N Manager API error (requestDeletion): ${response.status} ${response.statusText}`,
      );
    }

    return response.json() as Promise<OrderResponse>;
  }

  // ── GET /v1/orders/validate-uri-prefix ──────────────────────────────────────

  async validateUriPrefix(uriPrefix: string): Promise<CheckUriPrefixResponse> {
    const proxyBase = await this.getProxyBase();
    const url = `${proxyBase}/v1/orders/validate-uri-prefix?uriPrefix=${encodeURIComponent(uriPrefix)}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(
        `N8N Manager API error (validateUriPrefix): ${response.status} ${response.statusText}`,
      );
    }

    return response.json() as Promise<CheckUriPrefixResponse>;
  }

  // ── GET /v1/orders/existing-by-costcenter ───────────────────────────────────

  async checkExistingByCostCenter(
    costCenter: string,
  ): Promise<CheckExistingByCostCenterResponse> {
    const proxyBase = await this.getProxyBase();
    const url = `${proxyBase}/v1/orders/existing-by-costcenter?costcenter=${encodeURIComponent(costCenter)}`;

    const response = await this.fetchApi.fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(
        `N8N Manager API error (checkExistingByCostCenter): ${response.status} ${response.statusText}`,
      );
    }

    return response.json() as Promise<CheckExistingByCostCenterResponse>;
  }
}
